import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import {
  Sparkles,
  KeyRound,
  ExternalLink,
  Eye,
  EyeOff,
  Loader2,
  AlertCircle,
  Info,
  Crown,
  Database,
  Copy,
  Check,
} from 'lucide-react';
import {
  Card,
  CardTitle,
  CardDescription,
  Input,
  Button,
  Skeleton,
} from '@/components/ui';
import { ScoreBadge } from '@/components/keyword';
import { api } from '@/lib/api';
import { formatNumber } from '@keywordgod/shared';
import { cn } from '@/lib/utils';
import { useUserPrefs } from '@/lib/userPrefs';
import { PrefilledKeyField } from '@/components/PrefilledKeyField';
import { ApiKeyGate } from '@/components/ApiKeyGate';

/**
 * Phase 38: 블로그 코치
 * 사용자 본인의 Google Gemini API 키 + 블로그 URL 입력 → AI 기반 키워드 30개 추천.
 * 우리 quota 0 소모. Gemini 호출은 사용자 키로.
 */
export function BlogCoach() {
  const prefs = useUserPrefs();
  const [blogUrl, setBlogUrl] = useState(prefs.defaultBlogUrl);
  const hasKey = !!prefs.geminiApiKey;

  const m = useMutation({
    mutationFn: () =>
      api.blogCoach.analyze({
        blogUrl: blogUrl.trim(),
        geminiApiKey: prefs.geminiApiKey.trim(),
      }),
  });

  const canSubmit = !!blogUrl.trim() && hasKey && !m.isPending;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-extrabold flex items-center gap-2">
          <Sparkles className="h-7 w-7 text-gold" />
          <span className="text-gold-gradient">블로그 코치</span>
          <span className="ml-2 rounded-full bg-gold/15 px-2 py-0.5 text-[11px] font-bold text-gold">
            BETA
          </span>
        </h1>
        <p className="mt-1 text-sm text-gray-400">
          본인 네이버 블로그 + 본인 Gemini API 키로 — AI 가 블로그를 상세 분석해서 지금 올릴 키워드 30개를 추천합니다.
        </p>
      </header>

      <ApiKeyGate hasKey={hasKey} />

      {hasKey && (
      <Card>
        <CardTitle className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-gold" /> 블로그 정보 입력
        </CardTitle>
        <CardDescription>
          분석할 블로그 URL 만 입력하세요. Gemini 키는 설정에서 자동 사용됩니다.
        </CardDescription>

        <form
          className="mt-5 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) m.mutate();
          }}
        >
          <PrefilledKeyField
            label="네이버 블로그 URL 또는 ID"
            storedValue={prefs.defaultBlogUrl}
            value={blogUrl}
            onChange={setBlogUrl}
            placeholder="예: blog.naver.com/myblog 또는 myblog"
          />

          <div className="pt-2 flex items-center gap-3">
            <Button type="submit" variant="gold" disabled={!canSubmit}>
              {m.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> AI 분석 중…
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" /> 키워드 30개 추천받기
                </>
              )}
            </Button>
            {m.isPending && (
              <span className="text-xs text-gray-500">
                RSS 수집 → Gemini 분석 → 우리 DB enrichment (~10–20초)
              </span>
            )}
          </div>
        </form>

        {m.isError && (
          <p className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
            <AlertCircle className="inline h-4 w-4 mr-1" />
            {(m.error as Error).message}
          </p>
        )}
      </Card>
      )}

      {m.isPending && <LoadingSkeleton />}
      {m.data && <ResultSection data={m.data} />}
    </div>
  );
}

// ─── 로딩 스켈레톤 ───────────────────────────────────────────

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      <Card>
        <Skeleton className="h-6 w-64" />
        <div className="mt-3 space-y-2">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      </Card>
      <Card>
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </Card>
    </div>
  );
}

// ─── 결과 표시 ───────────────────────────────────────────────

interface AnalyzeResult {
  blog: {
    userId: string;
    blogName: string | null;
    postCount: number;
    recentPosts: Array<{
      title: string;
      category: string | null;
      pubDate: string;
      link: string;
    }>;
  };
  profile: {
    summary: string;
    topic: string;
    audience: string;
    style: string;
    categories: string[];
  };
  recommendations: Array<{
    keyword: string;
    reason: string;
    godScore: number | null;
    grade: string | null;
    totalSearch: number | null;
    saturation: number | null;
    inOurDb: boolean;
  }>;
  enrichmentCount: number;
  analysis: {
    recentPostCount: number;
    olderPostCount: number;
    recentCutoff: string;
    upcomingSeasons: Array<{
      name: string;
      emoji: string | null;
      state: 'active' | 'upcoming' | 'past';
      daysUntilStart: number;
      startsMd: string;
    }>;
  };
}

function ResultSection({ data }: { data: AnalyzeResult }) {
  return (
    <div className="space-y-4">
      <Card>
        <CardTitle className="flex items-center gap-2">
          <Crown className="h-5 w-5 text-gold" />
          <span>{data.blog.blogName ?? data.blog.userId}</span>
          <span className="text-xs font-normal text-gray-500">
            — 최근 {data.blog.postCount}개 포스트 AI 분석
          </span>
        </CardTitle>

        <div className="mt-4 space-y-3">
          <div className="rounded-lg border border-gold/30 bg-gold/5 px-4 py-3">
            <div className="text-xs text-gold font-bold">AI 가 파악한 블로거 정체성</div>
            <p className="mt-1 text-sm text-white">"{data.profile.summary}"</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
            <ProfileField label="주요 주제" value={data.profile.topic} />
            <ProfileField label="타깃 독자" value={data.profile.audience} />
            <ProfileField label="글 스타일" value={data.profile.style} />
          </div>
          {data.profile.categories?.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-gray-400 mr-1">탐지된 카테고리:</span>
              {data.profile.categories.map((c) => (
                <span
                  key={c}
                  className="rounded-full border border-gray-700 bg-gray-900 px-2 py-0.5 text-[11px] text-gray-200"
                >
                  {c}
                </span>
              ))}
            </div>
          )}
        </div>
      </Card>

      <Card className="border-gold/20 bg-gold/5">
        <CardTitle className="text-sm">분석에 반영된 데이터</CardTitle>
        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
          <div className="rounded-lg border border-gray-800 bg-gray-950 px-3 py-2">
            <div className="text-[10px] uppercase tracking-widest text-gray-500">최근 글 (제외 대상)</div>
            <div className="mt-1 text-sm text-white">
              <span className="tabular text-base font-bold text-gold">{data.analysis.recentPostCount}</span>
              <span className="ml-1 text-gray-400">개 · {data.analysis.recentCutoff} 이후</span>
            </div>
            <div className="mt-1 text-[10px] text-gray-500">이 글의 주제는 추천에서 자동 제외</div>
          </div>
          <div className="rounded-lg border border-gray-800 bg-gray-950 px-3 py-2">
            <div className="text-[10px] uppercase tracking-widest text-gray-500">옛 글 (재방문 가능)</div>
            <div className="mt-1 text-sm text-white">
              <span className="tabular text-base font-bold text-gold">{data.analysis.olderPostCount}</span>
              <span className="ml-1 text-gray-400">개 · 2026 이전</span>
            </div>
            <div className="mt-1 text-[10px] text-gray-500">정보 변경 가능성 — 같은 주제 재작성 OK</div>
          </div>
          <div className="rounded-lg border border-gray-800 bg-gray-950 px-3 py-2">
            <div className="text-[10px] uppercase tracking-widest text-gray-500">다가오는 시즌 (90일)</div>
            <div className="mt-1 text-sm text-white">
              <span className="tabular text-base font-bold text-gold">{data.analysis.upcomingSeasons.length}</span>
              <span className="ml-1 text-gray-400">개 이벤트</span>
            </div>
            <div className="mt-1 text-[10px] text-gray-500">미리 작성용 키워드 우선 추천</div>
          </div>
        </div>
        {data.analysis.upcomingSeasons.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {data.analysis.upcomingSeasons.slice(0, 8).map((ev, i) => (
              <span
                key={i}
                className={cn(
                  'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px]',
                  ev.state === 'active'
                    ? 'border-emerald-400/40 bg-emerald-400/10 text-emerald-300'
                    : 'border-gold/30 bg-gold/5 text-gold',
                )}
              >
                <span>{ev.emoji ?? '📅'}</span>
                <span>{ev.name}</span>
                <span className="text-gray-500">
                  {ev.state === 'active' ? '진행 중' : `D-${ev.daysUntilStart}`}
                </span>
              </span>
            ))}
          </div>
        )}
      </Card>

      <Card>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-gold" /> 지금 올려야 하는 키워드 TOP {data.recommendations.length}
            </CardTitle>
            <CardDescription>
              AI 가 생성한 30개 중 <Database className="inline h-3 w-3" /> 우리 분석 DB 매칭{' '}
              <span className="text-emerald-300 font-bold">{data.enrichmentCount}건</span> 은 검색량·점수 포함
            </CardDescription>
          </div>
          <CopyKeywordsButton keywords={data.recommendations.map((r) => r.keyword)} />
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b border-gray-800">
                <th className="py-2 pr-2 font-medium w-10 text-right">#</th>
                <th className="py-2 pr-2 font-medium">키워드 + 추천 이유</th>
                <th className="py-2 pr-2 font-medium text-right">월 검색량</th>
                <th className="py-2 pr-2 font-medium text-right">GodScore</th>
                <th className="py-2 font-medium text-right">등급</th>
              </tr>
            </thead>
            <tbody>
              {data.recommendations.map((r, i) => (
                <tr
                  key={r.keyword + i}
                  className={cn(
                    'border-b border-gray-900 last:border-b-0 hover:bg-gray-950/60',
                    !r.inOurDb && 'bg-gray-950/40',
                  )}
                >
                  <td className="py-2 pr-2 text-right tabular text-xs text-gray-500">{i + 1}</td>
                  <td className="py-2 pr-2 max-w-[400px]">
                    <a
                      href={`/analyze/${encodeURIComponent(r.keyword)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-white hover:text-gold inline-flex items-center gap-1.5"
                      title="새 탭에서 분석 — 추천 목록은 유지됩니다"
                    >
                      <span className="truncate">{r.keyword}</span>
                      {r.inOurDb && (
                        <Database
                          className="h-3 w-3 shrink-0 text-emerald-400"
                          aria-label="DB 매칭됨"
                        />
                      )}
                    </a>
                    {r.reason && (
                      <div className="mt-0.5 text-[11px] text-gray-400 leading-snug">
                        {r.reason}
                      </div>
                    )}
                  </td>
                  <td className="py-2 pr-2 text-right tabular text-gray-300">
                    {r.totalSearch != null ? formatNumber(r.totalSearch) : '-'}
                  </td>
                  <td className="py-2 pr-2 text-right tabular font-bold text-gold">
                    {r.godScore != null ? r.godScore : '-'}
                  </td>
                  <td className="py-2 text-right">
                    {r.grade ? (
                      <ScoreBadge grade={r.grade as never} size="sm" />
                    ) : (
                      <span className="text-[10px] text-gray-600 uppercase">신규</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="mt-3 text-[11px] text-gray-500">
          <Database className="inline h-3 w-3 mr-1 text-emerald-400" />
          아이콘 = 우리 DB 에 분석된 키워드 (점수/검색량 신뢰 가능) · 그 외 = AI 추천 신규 키워드
          (클릭 시 자동 분석 시작)
        </p>
      </Card>
    </div>
  );
}

function ProfileField({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-gray-800 bg-gray-950 px-3 py-2">
      <div className="text-[10px] uppercase tracking-widest text-gray-500">{label}</div>
      <div className="mt-0.5 text-sm text-white">{value}</div>
    </div>
  );
}

// ─── 키워드 30개 전체 복사 버튼 ──────────────────────────────

function CopyKeywordsButton({ keywords }: { keywords: string[] }) {
  const [copied, setCopied] = useState(false);

  async function onCopy() {
    if (keywords.length === 0) return;
    const text = keywords.join(', ');
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // 보안/구버전 브라우저 fallback — textarea 트릭
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
      } catch {
        // 실패해도 무시
      }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <button
      type="button"
      onClick={onCopy}
      disabled={keywords.length === 0}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors',
        copied
          ? 'border-emerald-400/60 bg-emerald-400/15 text-emerald-200'
          : 'border-gold/40 bg-gold/10 text-gold hover:bg-gold/20',
        keywords.length === 0 && 'opacity-50 cursor-not-allowed',
      )}
      title={`키워드 ${keywords.length}개를 콤마(,) 로 구분해 클립보드에 복사`}
    >
      {copied ? (
        <>
          <Check className="h-3.5 w-3.5" />
          복사됨!
        </>
      ) : (
        <>
          <Copy className="h-3.5 w-3.5" />
          키워드 {keywords.length}개 복사
        </>
      )}
    </button>
  );
}
